import React, { useEffect, useState } from "react";
import {
    Page,
    Layout,
    Card,
    TextField,
    Button,
    Text,
    BlockStack,
    InlineStack,
    Modal,
    Checkbox,
    Spinner,
    Banner
} from "@shopify/polaris";
import Http from "../Lib/Http";
import {useGoTo} from "@admin/Lib/useGoTo";
import Alert from "@admin/Lib/Alert";

function SizingSync() {
    const [shopDomain, setShopDomain] = useState('');
    const [syncPassword, setSyncPassword] = useState('');
    const [syncs, setSyncs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [modalActive, setModalActive] = useState(false);

    const [shopIdInput, setShopIdInput] = useState('');
    const [passwordInput, setPasswordInput] = useState('');
    const [syncConditions, setSyncConditions] = useState(true);

    const [error, setError] = useState<string | null>(null);

    const goTo = useGoTo();

    useEffect(() => {
        loadSyncInfo();
    }, []);

    const loadSyncInfo = () => {
        setLoading(true);
        Http.get('/app/api/v1/shop.json') // Replace with actual endpoint
            .then(response => {
                setShopDomain(response.data.shop.shop_domain	 || '');
                setSyncPassword(response.data.shop.sync_key || '');
                setLoading(false);
            })
            .catch(() => {
                setError('Unable to load sync settings.');
                setLoading(false);
            });
    };

    const handleAddSync = () => {
       setLoading(true);
        setError(null);
        Http.post('/app/api/v1/sync', {
            shop_id: shopIdInput,
            password: passwordInput,
        }).then(() => {
            setModalActive(false);
            setSuccess(true);
            Alert.success("Chart imported");
         }).catch((error) => {
            if (error.response && error.response.data && error.response.data.error) {
                console.log(error.response.data.error);
                setError(error.response.data.error);
                setModalActive(false);
                setSuccess(false);
            } else {
                setError('Failed to create sync connection.');
            }
            setLoading(false);
        });
    };

    return (
        <Page title="Import size charts from another store" backAction={{content: 'Home', onAction: () => goTo('')}}>
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner title="Unable to import charts" tone="critical" onDismiss={() => setError(null)}>
                            <p>{error}</p>
                        </Banner>
                    </Layout.Section>
                )}
                {success && (
                    <Layout.Section>
                        <Banner title="Charts imported" tone="success" onDismiss={() => setSuccess(false)}>
                            <p>Charts have been successfully imported</p>
                        </Banner>
                    </Layout.Section>
                )}
                <Layout.Section>
                    <Card roundedAbove="sm" padding="400">
                        <BlockStack gap="200">
                            <Text variant="headingMd" as="h6">Size Chart Sync</Text>
                            <p>
                                If you manage multiple stores and want to share size charts between them, you can
                                link shops by using a sync code. Once connected, you can import all charts
                            </p>

                            <TextField
                                autoComplete="off"
                                label="This shop's ID"
                                value={shopDomain}
                                readOnly
                                helpText="Use this ID in another store to import its charts."
                            />
                            <TextField
                                autoComplete="off"
                                label="This shop's sync code"
                                value={syncPassword}
                                readOnly
                                helpText="Use this code in another store to import its charts"
                            />
                            <div style={{marginBottom: '20px;'}}></div>
                            <InlineStack>
                                <Button onClick={() => setModalActive(true)}>
                                    Import size charts from another shop
                                </Button>
                            </InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>

            <Modal
                open={modalActive}
                onClose={() => setModalActive(false)}
                title="Link a shop for syncing"
                primaryAction={{
                    content: 'Import',
                    onAction: handleAddSync,
                    disabled: !shopIdInput || !passwordInput || loading,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => setModalActive(false),
                    }
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="200">
                        <p>
                            Enter the Shop ID and sync code of the store you'd like to import charts from another store.
                            All size charts will be copied.
                        </p>
                        <TextField
                            autoComplete="off"
                            label="Shop ID"
                            value={shopIdInput}
                            onChange={setShopIdInput}
                            helpText="The store identifier you're importing charts from."
                        />
                        <TextField
                            autoComplete="off"
                            label="Sync code"
                            value={passwordInput}
                            onChange={setPasswordInput}
                            type="password"
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </Page>
    );
}

export default SizingSync;
